import { useEffect, useRef, useState } from 'react';
import LeadStreamsService from 'src/services/LeadStreamsService';
import EventService from 'src/services/EventService';
import { getStreamDisplayName } from 'src/utils/leadStreams';

import {
    commandCandidates,
    commandResult,
    commandStateMatches,
    commandUnavailable,
    readCommandStream,
} from './streamCommand';

import type { StreamCommandIntent, StreamCommandTarget } from './streamCommand';
import type { CompanionReply } from './model';
import type { Stream } from 'src/services/LeadStreamsService/agent';

type CommandState =
    | { phase: 'select'; intent: StreamCommandIntent; streams: Stream[] }
    | { phase: 'loading' }
    | { phase: 'ready'; target: StreamCommandTarget; stream: Stream; note?: string }
    | { phase: 'saving'; target: StreamCommandTarget; name: string }
    | { phase: 'uncertain'; target: StreamCommandTarget; text: string; checking?: boolean }
    | { phase: 'error'; text: string }
    | null;

const uncertainText =
    'Результат команды пока не подтверждён. Проверим состояние потока, не отправляя команду повторно.';
export const commandStorageKey = (userId: number) => `leadsmania:companion-command:${userId}`;
function readPending(userId: number): CommandState {
    try {
        const target = JSON.parse(sessionStorage.getItem(commandStorageKey(userId)) || 'null');
        if (target && Number.isSafeInteger(target.id) && target.id > 0 && typeof target.active === 'boolean')
            return { phase: 'uncertain', target: { id: target.id, active: target.active }, text: uncertainText };
    } catch {
        /* An unavailable journal cannot authorize a command. */
    }
    return null;
}

// A lost PATCH response must not become an automatic retry. Its late resolution is ignored.
async function bounded<T>(work: () => Promise<T>): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        if (!navigator.onLine) throw new Error('Нет связи.');
        return await Promise.race([
            work(),
            new Promise<never>((_resolve, reject) => {
                timer = setTimeout(() => reject(new Error('Timeout')), 20000);
            }),
        ]);
    } finally {
        clearTimeout(timer);
    }
}

export default function useStreamCommand(userId: number, pageScope: string, reply: (value: CompanionReply) => void) {
    const [state, setState] = useState<CommandState>(() => readPending(userId));
    const current = useRef(state);
    const generation = useRef(0);
    const alive = useRef(true);
    const replyRef = useRef(reply);
    replyRef.current = reply;
    const set = (next: CommandState) => {
        current.current = next;
        if (alive.current) setState(next);
    };
    const locked = () => current.current?.phase === 'saving' || current.current?.phase === 'uncertain';
    const cancel = () => {
        if (locked()) return;
        generation.current += 1;
        set(null);
    };
    useEffect(() => {
        cancel();
    }, [pageScope]); // A prepared confirmation belongs to its original page.
    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
            generation.current += 1;
        };
    }, []);
    const isCurrent = (id: number) => alive.current && generation.current === id;
    const fetchStream = async (id: number) =>
        readCommandStream(await bounded(() => LeadStreamsService.getStream(id)), id, userId);
    const finish = (stream: Stream, changed: boolean) => {
        try {
            sessionStorage.removeItem(commandStorageKey(userId));
        } catch {
            /* Read-only recovery remains safe. */
        }
        set(null);
        replyRef.current({
            text: commandResult(stream, changed),
            actions: [{ label: 'Открыть поток', path: `/leadstream/${stream.id}` }],
        });
        EventService.dispatch('LEAD_STREAMS_UPDATED', stream);
    };
    const prepare = async (id: number, active: boolean) => {
        if (locked()) return;
        const run = ++generation.current;
        set({ phase: 'loading' });
        try {
            const stream = await fetchStream(id);
            if (!isCurrent(run)) return;
            const unavailable = commandUnavailable(stream, active);
            if (unavailable) {
                set(null);
                replyRef.current({
                    text: `«${getStreamDisplayName(stream)}» · поток №${id}\n${unavailable}`,
                    actions: [{ label: 'Открыть поток', path: `/leadstream/${id}` }],
                });
                return;
            }
            if (commandStateMatches(stream, active)) {
                finish(stream, false);
                return;
            }
            set({ phase: 'ready', target: { id, active }, stream });
        } catch {
            if (isCurrent(run))
                set({ phase: 'error', text: 'Не удалось проверить поток. Команда не отправлена. Попробуйте ещё раз.' });
        }
    };
    const begin = async (intent: StreamCommandIntent, currentId?: number) => {
        if (current.current) return;
        const run = ++generation.current;
        set({ phase: 'loading' });
        try {
            const streams = await bounded(() => LeadStreamsService.getStreams());
            if (!isCurrent(run)) return;
            const candidates = commandCandidates(intent, streams, currentId);
            if (candidates.length === 1) {
                await prepare(candidates[0].id, intent.active);
                return;
            }
            if (!candidates.length) {
                set({
                    phase: 'error',
                    text: 'Не удалось однозначно найти поток. Откройте его страницу или укажите точное название либо номер.',
                });
                return;
            }
            set({ phase: 'select', intent, streams: candidates });
        } catch {
            if (isCurrent(run)) set({ phase: 'error', text: 'Не удалось загрузить потоки. Команда не отправлена.' });
        }
    };
    const confirm = async () => {
        const ready = current.current;
        if (ready?.phase !== 'ready') return;
        const run = ++generation.current;
        const { target, stream: previous } = ready;
        set({ phase: 'loading' });
        let submitted = false;
        try {
            const stream = await fetchStream(target.id);
            if (!isCurrent(run)) return;
            const unavailable = commandUnavailable(stream, target.active);
            if (unavailable) {
                set({ phase: 'error', text: unavailable });
                return;
            }
            if (commandStateMatches(stream, target.active)) {
                finish(stream, false);
                return;
            }
            if (
                getStreamDisplayName(stream) !== getStreamDisplayName(previous) ||
                stream.is_active !== previous.is_active
            ) {
                set({ phase: 'ready', target, stream, note: 'Данные потока изменились. Проверьте действие ещё раз.' });
                return;
            }
            // Save before sending, so reload restores a check, never a repeat mutation.
            sessionStorage.setItem(commandStorageKey(userId), JSON.stringify(target));
            submitted = true;
            set({ phase: 'saving', target, name: getStreamDisplayName(stream) });
            const updated = readCommandStream(
                await bounded(() => LeadStreamsService.updateStream(target.id, { is_active: target.active })),
                target.id,
                userId
            );
            if (!isCurrent(run)) return;
            if (!commandStateMatches(updated, target.active)) throw new Error('Unexpected state');
            const verified = await fetchStream(target.id);
            if (!isCurrent(run)) return;
            if (!commandStateMatches(verified, target.active)) throw new Error('State changed');
            finish(verified, true);
        } catch {
            if (isCurrent(run))
                set(
                    submitted
                        ? { phase: 'uncertain', target, text: uncertainText }
                        : {
                              phase: 'error',
                              text: 'Не удалось подготовить изменение. Команда не отправлена. Попробуйте ещё раз.',
                          }
                );
        }
    };
    const check = async () => {
        const uncertain = current.current;
        if (uncertain?.phase !== 'uncertain' || uncertain.checking) return;
        const run = ++generation.current;
        set({ ...uncertain, checking: true });
        try {
            const stream = await fetchStream(uncertain.target.id);
            if (!isCurrent(run)) return;
            EventService.dispatch('LEAD_STREAMS_UPDATED', stream);
            if (commandStateMatches(stream, uncertain.target.active)) {
                finish(stream, false);
                return;
            }
            set({
                ...uncertain,
                checking: false,
                text: `Сейчас «${getStreamDisplayName(stream)}» ${
                    stream.is_active ? 'включён' : 'на паузе'
                }. Запрошенное изменение пока не подтверждено. Проверку можно повторить.`,
            });
        } catch {
            if (isCurrent(run))
                set({
                    ...uncertain,
                    checking: false,
                    text: 'Связь с потоком недоступна. Результат команды неизвестен. Повторите проверку позже.',
                });
        }
    };
    return {
        state,
        begin,
        prepare,
        confirm,
        check,
        cancel,
        busy:
            state?.phase === 'loading' ||
            state?.phase === 'saving' ||
            (state?.phase === 'uncertain' && !!state.checking),
    };
}
